import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarPlus, CheckCircle2, Clock, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ExtendRequest() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const src = params.get("src") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [sel, setSel] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/extend/${token}${src ? `?src=${src}` : ""}`).then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid or has expired."));
  }, [token, src]);

  const oneDayLocked = info?.oneday_locked ?? false;
  const oneDayUsed = info?.oneday_used ?? 0;
  const oneDayRemaining = info?.oneday_remaining ?? 3;
  const opts = info?.options || {};

  const newDate = (d) => {
    if (!info) return "";
    const dt = new Date(info.current_return_date + "T00:00:00");
    dt.setDate(dt.getDate() + d);
    return dt.toISOString().slice(0, 10);
  };

  const submit = async () => {
    if (!sel) return toast.error("Please choose an option first.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/extend/${token}/request`, { days: sel, note, ack_calendar_day: true, src });
      setDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit your request. Please try again.");
    } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <span className="font-display font-black text-xl">AutoHire <span className="text-[#F2B705]">STL</span></span>
        </div>
        {children}
      </div>
    </div>
  );

  if (error) return shell(
    <div data-testid="extend-error" className="bg-[#0C0C0C] border border-red-500/30 rounded-xl p-8 text-center">
      <AlertTriangle className="mx-auto text-red-400 mb-3" size={28} />
      <p className="text-white/70">{error}</p>
      <p className="text-white/40 text-sm mt-3">Need help? Call us at (314) 936-1837.</p>
    </div>
  );
  if (!info) return shell(<div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>);

  if (done || info.pending_request) {
    const p = done || info.pending_request;
    return shell(
      <div data-testid="extend-pending" className="bg-[#0C0C0C] border border-[#F2B705]/30 rounded-xl p-8 text-center">
        {done ? <CheckCircle2 className="mx-auto text-green-400 mb-3" size={30} /> : <Clock className="mx-auto text-[#F2B705] mb-3" size={30} />}
        <h1 className="font-display font-bold text-xl mb-2">{done ? "Request Received!" : "Extension Request Pending"}</h1>
        <p className="text-white/60 text-sm leading-relaxed">
          Your request to extend the <b className="text-white">{info.vehicle}</b>
          {p.new_return_date ? <> to <b className="text-[#F2B705]">{p.new_return_date}</b></> : null}
          {" "}({p.days} extra day{p.days !== 1 ? "s" : ""} · est. {money(p.estimated_total)}) is with our team.
          We'll confirm by text or email shortly.
        </p>
      </div>
    );
  }

  const OptionBtn = ({ days, title, sub, price, disabled, testid }) => (
    <button data-testid={testid} disabled={disabled} onClick={() => setSel(days)}
      className={`w-full flex items-center justify-between gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors
        ${disabled ? "border-white/10 bg-white/[0.02] opacity-45 cursor-not-allowed"
          : sel === days ? "border-[#F2B705] bg-[#F2B705]/[0.08]"
          : "border-white/15 bg-[#0A0A0A] hover:border-[#F2B705]/50"}`}>
      <span>
        <span className="block font-bold text-sm">{title}</span>
        <span className={`block text-xs mt-0.5 ${disabled ? "text-red-300" : "text-white/45"}`}>{sub}</span>
      </span>
      <span className="font-bold text-[#F2B705] whitespace-nowrap">{money(price)}</span>
    </button>
  );

  return shell(
    <div data-testid="extend-request-page" className="bg-[#0C0C0C] border border-white/10 rounded-xl p-7">
      <h1 className="font-display font-bold text-xl flex items-center gap-2 mb-1"><CalendarPlus size={20} className="text-[#F2B705]" /> Extend Your Rental</h1>
      <p className="text-white/50 text-sm mb-3">Hi {(info.name || "there").split(" ")[0]} — need more time with your <b className="text-white">{info.vehicle}</b>?</p>
      <p className="text-white/45 text-xs mb-4 leading-relaxed">
        Up to <b className="text-white">3 one-day extensions</b> are available per rental. Once you've used all 3,
        or once you request a <b className="text-white">3-day or weekly extension</b>, 1-day extensions are no
        longer offered — but you can keep requesting <b className="text-white">3-day or weekly extensions with
        no limit</b>, each subject to approval, payment, and vehicle availability.
      </p>

      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-sm mb-3 flex justify-between">
        <span className="text-white/50">Current return date</span>
        <span className="font-semibold">{info.current_return_date}</span>
      </div>

      <div data-testid="extensions-remaining-pill"
        className={`rounded-lg border px-4 py-2.5 text-sm mb-4 flex items-center justify-between ${oneDayLocked ? "border-amber-500/50 bg-amber-500/10 text-amber-300 font-bold" : "border-[#F2B705]/40 bg-[#F2B705]/[0.06]"}`}>
        <span>{oneDayLocked ? "1-Day Extensions Used Up" : "1-Day Extensions Remaining:"}</span>
        <span className="font-black">{oneDayLocked ? `${oneDayUsed} of 3 used` : `${oneDayRemaining} — ${oneDayUsed} of 3 used`}</span>
      </div>

      {oneDayLocked && (
        <p data-testid="limit-reached-msg" className="text-amber-300 text-xs mb-4 leading-relaxed">
          {info.oneday_limit_message || "You've used all 3 one-day extensions on this rental. 3-day and weekly extensions are still available below."}
        </p>
      )}

      <div className="space-y-2 mb-4">
        {!oneDayLocked && (
          <OptionBtn days={1} testid="extend-opt-1day"
            title="Extend +1 Day"
            sub={`New return: ${newDate(1)} · ${oneDayUsed} of 3 one-day extensions used`}
            price={opts.one_day} />
        )}
        <OptionBtn days={3} testid="extend-opt-3day"
          title="3-Day Extension"
          sub={`New return: ${newDate(3)} · no limit on 3-day/weekly extensions`}
          price={opts.three_day} />
        <OptionBtn days={7} testid="extend-opt-weekly"
          title="Weekly Extension (7 days)"
          sub={`New return: ${newDate(7)} · no limit on 3-day/weekly extensions`}
          price={opts.weekly} />
      </div>

      {sel > 0 && (
        <div data-testid="extend-quote" className="rounded-lg border border-[#F2B705]/40 bg-[#F2B705]/[0.06] px-4 py-3 text-sm mb-4 flex justify-between font-bold">
          <span>{sel === 1 ? "1-Day Extension" : sel === 3 ? "3-Day Extension" : "Weekly Extension"} — returns {newDate(sel)}</span>
          <span data-testid="extend-quote-total" className="text-[#F2B705]">{money(sel === 1 ? opts.one_day : sel === 3 ? opts.three_day : opts.weekly)}</span>
        </div>
      )}

      <label className="text-xs uppercase tracking-widest text-white/45">Note for our team <span className="text-white/30 normal-case">(optional)</span></label>
      <textarea data-testid="extend-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        className="w-full mt-1 mb-4 bg-[#0A0A0A] border border-white/15 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#F2B705] resize-none" />

      <button data-testid="extend-submit" onClick={submit} disabled={busy || !sel}
        className="w-full bg-[#F2B705] text-black font-bold py-3 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <CalendarPlus size={16} />} Request {sel === 3 ? "New 3-Day Rental" : sel === 7 ? "New Weekly Rental" : "Extension"}
      </button>
      <p className="text-white/30 text-[11px] text-center mt-3">This is a request — your extension is confirmed once our team approves it.</p>
      <p data-testid="extension-policy-note" className="text-white/35 text-[11px] text-center mt-2 leading-relaxed border-t border-white/5 pt-3">
        <b className="text-white/50">Extension Policy:</b> {info.policy}
      </p>
    </div>
  );
}
